import { useEffect, useRef, useState } from 'react'
import { Captions, CaptionsOff, Maximize, Minimize } from 'lucide-react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog'
import { supportService, type TrainingMaterial } from '@/services/api/supportService'
import { apiClient } from '@/services/apiClient'
import { cn } from '@/lib/utils'

interface GuidanceVideoModalProps {
  material: TrainingMaterial
  open: boolean
  onOpenChange: (open: boolean) => void
}

/**
 * Plays a guidance walkthrough video in a dialog. Resolves the SAS video +
 * caption URLs the same way the Training section does (SAS URL in prod; an
 * authenticated blob fetch in local dev where SAS isn't available).
 */
export function GuidanceVideoModal({ material, open, onOpenChange }: GuidanceVideoModalProps) {
  const [videoUrl, setVideoUrl] = useState<string | null>(null)
  const [captionUrl, setCaptionUrl] = useState<string | null>(null)
  const objectUrls = useRef<string[]>([])
  const videoRef = useRef<HTMLVideoElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [activeCue, setActiveCue] = useState('')
  const [showCaptions, setShowCaptions] = useState(true)
  const [isFullscreen, setIsFullscreen] = useState(false)

  useEffect(() => {
    if (!open) return
    let cancelled = false

    const resolve = async (
      getter: Promise<{ video_url?: string; caption_url?: string }>,
      pick: (r: any) => string | undefined,
      forceVtt: boolean,
    ): Promise<string | null> => {
      try {
        const result = await getter
        let url = pick(result)
        if (!url) return null
        if (url.startsWith('/')) {
          // Local-dev fallback: no SAS — fetch via the authenticated client and
          // make a blob URL (<video>/<track> src can't send auth headers).
          const response = await apiClient.get(url, { responseType: 'blob' })
          const blob = forceVtt
            ? new Blob([response.data as Blob], { type: 'text/vtt' })
            : (response.data as Blob)
          url = URL.createObjectURL(blob)
          objectUrls.current.push(url)
        }
        return url
      } catch {
        return null
      }
    }

    ;(async () => {
      // View analytics (best-effort; never blocks playback)
      supportService.getTrainingMaterial(material.material_id).catch(() => {})

      if (material.video_blob_path) {
        const v = await resolve(supportService.getTrainingVideoUrl(material.material_id), (r) => r.video_url, false)
        if (!cancelled) setVideoUrl(v)
        const c = await resolve(supportService.getTrainingCaptionUrl(material.material_id), (r) => r.caption_url, true)
        if (!cancelled && c) setCaptionUrl(c)
      } else if (material.video_url) {
        if (!cancelled) setVideoUrl(material.video_url)  // external/embed URL
      }
    })()

    return () => {
      cancelled = true
      objectUrls.current.forEach((u) => URL.revokeObjectURL(u))
      objectUrls.current = []
      setVideoUrl(null)
      setCaptionUrl(null)
    }
  }, [open, material.material_id, material.video_blob_path, material.video_url])

  // Captions: keep the track 'hidden' so it still parses + fires cuechange but the
  // browser never paints its oversized native cues. We render our own inset
  // overlay (below) in BOTH windowed and fullscreen — because we fullscreen the
  // container (not the <video>), so the overlay + our controls ride along.
  useEffect(() => {
    const video = videoRef.current
    if (!open || !video || !captionUrl) return
    const track = video.textTracks[0]
    if (!track) return
    track.mode = 'hidden'
    const onCueChange = () => {
      const cues = track.activeCues ? Array.from(track.activeCues) : []
      setActiveCue(cues.map((c) => (c as VTTCue).text).join('\n'))
    }
    track.addEventListener('cuechange', onCueChange)
    return () => {
      track.removeEventListener('cuechange', onCueChange)
      setActiveCue('')
    }
  }, [open, captionUrl, videoUrl])

  // Mirror the browser's fullscreen state (we fullscreen the container so our
  // caption overlay + controls render on the fullscreen layer).
  useEffect(() => {
    const onChange = () => {
      const fsEl = document.fullscreenElement || (document as any).webkitFullscreenElement
      setIsFullscreen(!!fsEl && fsEl === containerRef.current)
    }
    document.addEventListener('fullscreenchange', onChange)
    document.addEventListener('webkitfullscreenchange', onChange as EventListener)
    return () => {
      document.removeEventListener('fullscreenchange', onChange)
      document.removeEventListener('webkitfullscreenchange', onChange as EventListener)
    }
  }, [])

  const toggleFullscreen = () => {
    const el = containerRef.current as any
    if (!el) return
    const fsEl = document.fullscreenElement || (document as any).webkitFullscreenElement
    if (fsEl) {
      (document.exitFullscreen || (document as any).webkitExitFullscreen)?.call(document)
    } else {
      (el.requestFullscreen || el.webkitRequestFullscreen)?.call(el)
    }
  }

  const isEmbed = !material.video_blob_path && !!material.video_url

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[95vw] sm:max-w-5xl">
        <DialogHeader>
          <DialogTitle>{material.title}</DialogTitle>
          {material.description && <DialogDescription>{material.description}</DialogDescription>}
        </DialogHeader>
        <div
          ref={containerRef}
          className={cn(
            'relative w-full overflow-hidden bg-black',
            isFullscreen ? 'flex items-center justify-center' : 'aspect-video rounded-lg',
          )}
        >
          {isEmbed ? (
            <iframe
              src={videoUrl ?? material.video_url}
              className="h-full w-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              title={material.title}
            />
          ) : videoUrl ? (
            <>
              {/* We supply our own caption + fullscreen controls, so strip the
                  native CC toggle and fullscreen button from the video chrome. */}
              <style>{`.guidance-player::-webkit-media-controls-toggle-closed-captions-button{display:none}`}</style>
              <video
                ref={videoRef}
                src={videoUrl}
                controls
                controlsList="nofullscreen"
                autoPlay
                crossOrigin="anonymous"
                className="guidance-player h-full w-full object-contain"
                preload="metadata"
              >
                {captionUrl && <track kind="subtitles" src={captionUrl} srcLang="en" label="English" />}
              </video>

              {/* Caption + fullscreen controls (top-right) — work in both modes
                  because the container, not the video, is what goes fullscreen. */}
              <div className="absolute right-2 top-2 z-10 flex items-center gap-1.5">
                {captionUrl && (
                  <button
                    type="button"
                    onClick={() => setShowCaptions((s) => !s)}
                    title={showCaptions ? 'Hide captions' : 'Show captions'}
                    aria-label={showCaptions ? 'Hide captions' : 'Show captions'}
                    aria-pressed={showCaptions}
                    className={cn(
                      'inline-flex h-8 w-8 items-center justify-center rounded-md border text-white backdrop-blur-sm transition-colors',
                      showCaptions ? 'border-white/40 bg-white/25 hover:bg-white/35' : 'border-white/20 bg-black/40 hover:bg-black/55',
                    )}
                  >
                    {showCaptions ? <Captions className="h-4 w-4" /> : <CaptionsOff className="h-4 w-4" />}
                  </button>
                )}
                <button
                  type="button"
                  onClick={toggleFullscreen}
                  title={isFullscreen ? 'Exit full screen' : 'Full screen'}
                  aria-label={isFullscreen ? 'Exit full screen' : 'Full screen'}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-white/20 bg-black/40 text-white backdrop-blur-sm transition-colors hover:bg-black/55"
                >
                  {isFullscreen ? <Minimize className="h-4 w-4" /> : <Maximize className="h-4 w-4" />}
                </button>
              </div>

              {showCaptions && activeCue && (
                <div className={cn('pointer-events-none absolute inset-x-0 flex justify-center px-[8%]', isFullscreen ? 'bottom-[10%]' : 'bottom-[12%]')}>
                  <span
                    className={cn(
                      'whitespace-pre-line rounded bg-black/75 px-3 py-1 text-center font-medium leading-snug text-white shadow-sm',
                      isFullscreen ? 'text-lg lg:text-2xl' : 'text-xs sm:text-sm md:text-base',
                    )}
                  >
                    {activeCue}
                  </span>
                </div>
              )}
            </>
          ) : (
            <div className="flex h-full items-center justify-center text-sm text-white/70">Loading…</div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
